import moment from "moment";
import { paymentOptionsEnum } from "../containers/Billing/Editor/BillSummary";
import { Client, Customer } from "./ClientInterface";
import { ResourceCentre } from "./ResourceCentreInterface";
import { StockTransactions } from "./StockInterfaces";
import { INSURANCE_TYPE } from "../actions/helpers/billHelper";
import { Bed } from "./BedInterface";

export const CurrentBillVersion = "2.0.0";

export enum VisitType {
  OPD = "opd",
  IPD = "ipd"
}

export type VisitTypes = VisitType.OPD | VisitType.IPD;

export enum DocumentTypes {
  INVOICE = "invoice",
  CREDITNOTE = "creditNote"
}

export enum BillStatus {
  draft = "draft",
  billed = "billed",
  cancelled = "cancelled",
  proformaDraft = "proformaDraft"
}

export enum DiscountBasedOn {
  invoice = "invoice",
  inline = "inline"
}

export enum QRPaymentReceiptMethods {
  khalti = "khalti",
  esewa = "esewa",
  fonepay = "fonepay"
}

export enum DiscountTypes {
  percentage = "percent",
  amount = "amount"
}

export enum TransactionType {
  DEBIT = "debit",
  CREDIT = "credit"
}

export enum BillItemFieldTypes {
  delivered = "delivered",
  returned = "returned",
  description = "description",
  batchInfo = "batchInfo",
  quantity = "quantity",
  unit = "unit",
  perUnit = "perUnit",
  discountPercent = "discountPercent",
  discountAmt = "discountAmt",
  VATPercent = "vatPct",
  grossTotal = "grossTotal",
  department = "department",
  info = "info",
  type = "type"
}

export enum BatchInfoFieldTypes {
  batchId = "batchId",
  quantity = "quantity"
}

export enum PrintTemplate {
  A5 = "A5Template",
  B4 = "B4",
  BankPaper = "bankPaper",
  Default = "defaultTemplate",
  Bordered = "borderedTemplate",
  RepeatingFooter = "fixedFooterTemplate",
  Government = "governmentTemplate",
  BlockCategory = "blockCategoryTemplate",
  CategoryHeader = "CategoryHeaderTemplate"
}

export type DocumentType = DocumentTypes.INVOICE | DocumentTypes.CREDITNOTE;

export type DiscountType = DiscountTypes.percentage | DiscountTypes.amount;

export type BillItemFieldType =
  | BillItemFieldTypes.delivered
  | BillItemFieldTypes.returned
  | BillItemFieldTypes.description
  | BillItemFieldTypes.batchInfo
  | BillItemFieldTypes.quantity
  | BillItemFieldTypes.unit
  | BillItemFieldTypes.perUnit
  | BillItemFieldTypes.discountPercent
  | BillItemFieldTypes.discountAmt
  | BillItemFieldTypes.VATPercent
  | BillItemFieldTypes.grossTotal
  | BillItemFieldTypes.department
  | BillItemFieldTypes.info
  | BillItemFieldTypes.type;

export type BatchInfoFieldType = BatchInfoFieldTypes.batchId | BatchInfoFieldTypes.quantity;

export type BillItemCalculationBasis = "perUnit" | "grossTotal";

export interface BillSummaryType {
  taxableAmount: number;

  // Existing fields for overall (VAT + HET)
  // NOTE: HET was introduced for short time before being removed by the government.
  taxAmount: number;
  taxableSales: number;
  taxAmtWithRates: Record<string, number>;

  // New fields for VAT only
  vatAmount: number;
  vatTaxableSales: number;
  vatAmtWithRates: Record<string, number>;

  // Existing common fields
  totalPerUnitTimesQty: number;
  totalAmount: number;
  roundOffAmt: number;
  inWords: string;
  discount: number;
  discountPct: number;
  discountType?: string | null;
  exemptSales: number;
}

export interface BatchInfoType {
  batchId: string;
  quantity: number;
  expiryDate: moment.Moment;
}

export enum StockUnitTypes {
  package = "package",
  unit = "unit"
}

export enum BillItemSource {
  stocks = "stocks",
  services = "services"
}

export interface BillItemType {
  sNo: number;
  uuid: string;
  returned?: boolean;
  productType: string;
  delivered: boolean;
  description: string;
  serviceProviderId: number | null;
  serviceProviderIds: number[];
  code: string;
  hsCode?: string;
  unit: string;
  perUnit: number;
  discountPercent: number;
  discountAmt: number;
  vatPct: number;
  vatAmtBeforeDiscount: number;
  vatAmtAfterDiscount: number;
  grossTotal: number;
  serviceProviderRateUnit: "percentage";
  stockUnitType?: StockUnitTypes;
  stockProductUnitsPerPackage?: number;
  productId: number;
  nonDiscountable: boolean;
  source: BillItemSource;
  productData?: {
    labCharge: number;
    materialCharge: number;
    name: string;
    id: number;
    source: string;
    servicePriceExcVAT: number;
    unitPriceExcVAT: number;
    isNarcotics: boolean;
  };
  subItems: {
    grossTotalPrice: number;
    description: string;
    productData: ProductData;
    productId: number;
    productisableId: number | null;
    productisableName: string;
    serviceProviderId: number | null;
    serviceProviderIds: number[];
  }[];
  salesLedgerId: number;
  departmentId: number;
  info?: string;
  note?: string;
  teeth?: number[];
  isMultiSession?: boolean;

  batchInfo: BatchInfoType;
  quantity: number;
  expiryDate: moment.Moment;

  batchId?: string;
}

export interface ProductData {
  category: string;
  grossTotalPrice: string;
  id: number;
  labCharge: number;
  materialCharge: number;
  name: string;
  productType: string;
  rates: { id: number; rate: number; unit: string; serviceId: number; serviceProviderId: number }[];
  resourceCentreId: number;
  servicePriceExcVAT: number;
  serviceProviderRateUnit: string;
  vatPct: number;
}

export interface BillDocumentSettingsType {
  showFields: BillItemFieldType[];
  discountSettings: {
    discountBasis: DiscountBasedOn;
    discountType: DiscountType;
  };
  extraColumnSettings: { [key: string]: boolean };
  remarksCompulsoryForDiscountedBill: boolean;
}

export interface PaymentBreakdown {
  method: paymentOptionsEnum;
  amount: number;
}

export interface BillPaymentInfoType {
  paid: boolean;
  paidAmount: number;
  tenderAmount?: number;
  changeAmount?: number;
  paymentBreakdown: Array<PaymentBreakdown>;
  paymentMethod?: paymentOptionsEnum; // for old bills which might still have single payment method
  paymentDistribution?: {
    additionalPayment: number;
    paymentFromBalance: number;
    paymentFromSsf?: number;
  };
}

export interface RefundInfoType {
  previousPaidAmt: number;
  previousDueAmt: number;
  maximumRefundable: number;
}

export interface BillClient {
  id: number;
  dob: string;
  email: string;
  phone: string;
  lastName: string;
  firstName: string;
}

export interface BillCustomer {
  id: number;
  clientId: number;
  customerNumber: string;
  externalIdentifier: string;
  client: { active: boolean };
}

export interface BillDocumentType {
  claimNumber: string;
  insuranceNumber: string;
  insuranceType?: INSURANCE_TYPE | boolean;
  id: number;
  visitType: VisitType;
  referencedBillNum?: number;
  version: string;
  type: DocumentType;
  isDraft: boolean;
  isInternal: boolean;
  title: string;
  billNumber: string | null;
  referredBy: string;
  issueDate: string;
  dueDate: string | null;
  remarks: string;
  fiscalYear: string;
  isSsfBilling: boolean;
  summary: BillSummaryType;
  billItems: Array<Partial<BillItemType>>;
  client: Client & { balance?: number };
  resourceCentre: {
    id: number;
    panNo: string;
  };
  settings: BillDocumentSettingsType;
  enteredBy: {
    userId: number;
    signature: string;
    name: string;
  };
  ssf: {
    id?: string;
    uuid?: string | null;
    employer?: {
      eSSID: string;
      name: string;
    };
    scheme: { label: string; value: number };
    subProduct: { label: string; value: number };
    balance: {
      medical: { opd: number; ipd: number; isActive: boolean };
      accident: { value: number; isActive: boolean };
    };
  };
  paymentInfo: BillPaymentInfoType;
  refundInfo?: RefundInfoType;
  referenceTo: number | null;
  referrerId?: number;
  isLocalStorageData?: boolean;
  extraReferrer?: { name: string; id: number };
  status: string;
  remindOn: {
    duration: number;
    format: string;
    note?: string;
  };
  createdLabTestIds?: Array<number>;
  createdLabTestNumbers?: Array<number>;
  createdMedicalTestIds?: Array<number>;
  createdMedicalTestNumbers?: Array<number>;
  isProformaBill: boolean;
  ipdId?: number;
  admissionDate?: string | Date;
  dischargeDate?: string | Date;
  bed?: Bed;
}

export interface BillForList {
  id: number;
  billNumber: string;
  client: BillClient;
  customer: BillCustomer;
  isDraft: boolean;
  issueDate: string; // ISO Date String
  insuranceNumber: string;
  claimNumber: string;
  paymentInfo: BillPaymentInfoType;
  referenceTo: number;
  referrers: string;
  related: number;
  status: BillStatus;
  summary: BillSummaryType;
  total: string;
}

export interface BillType {
  id: number;
  claimId?: string;
  insuranceType?: INSURANCE_TYPE;
  total?: number;
  paymentInfo: BillPaymentInfoType;
  document: BillDocumentType;
  status: BillStatus;
  referenceTo: number;
  // eslint-disable-next-line camelcase
  created_at: string;
  type: DocumentTypes;
  billNumber: number;
  insuranceNumber: string;
  claimNumber: string;
  printCount: number;
  customer: Customer;
  labId: number;
  related: BillType;
  creditNotes?: BillType[];
  issueDate: string;
  paidAmount: number;
  summary: BillSummaryType;
  isDraft: boolean;
  isProformaBill: boolean;
  uuid: string;
  relatedPurchaseEntry: Partial<StockTransactions>;
  resourceCentreId: number;
  referrerId?: number;
  referrers?: string;
  creditNoteRefunds?: Array<Record<string, unknown>>;
  receipts?: Receipt[];

  // related
  clientId: number;
  client?: Client;
}

export interface BillTypeByUUID extends BillType {
  resourceCentre: ResourceCentre;
  clientBalance: number;
}

export enum TrackingNumType {
  RECEIPT_NUMBER = "receiptNumber",
  REFUND_NUMBER = "refundNumber"
}

export interface TrackingNumInterface {
  active: boolean;
  id: number;
  lastNumber: number;
  nextNumber: number;
  type: string;
}

export interface Receipt {
  id: number;
  receiptId: number;
  paidAmount: number;
  billId: number;
  paymentMethod: string;
  receivedOn: string;
  remarks: string;
  cancelled: boolean;
  created_at: string | Date;
}
